import React, { Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { HelmetProvider } from 'react-helmet-async'
import { AuthProvider, useAuth } from './hooks/firebase/useAuth'
import { SubscriptionProvider } from './contexts/SubscriptionContext'
import { PricingProvider } from './contexts/PricingContext'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { LoadingSpinner } from './components/common/LoadingSpinner'
import { initializeAccessibility } from './utils/accessibility'
import { trackPageView } from './utils/ga4'
import { useReferralCapture } from './hooks/useReferralCapture'
import { resolveNextPath } from './utils/nextPath'

// Retry wrapper for lazy imports — handles stale chunk errors after deployments
function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return React.lazy(() =>
    factory().catch((err) => {
      // If the import failed (stale chunk), try one cache-busted reload
      const RELOAD_KEY = 'chunk-reload-ts'
      const lastReload = sessionStorage.getItem(RELOAD_KEY)
      const now = Date.now()

      if (!lastReload || now - Number(lastReload) > 10_000) {
        sessionStorage.setItem(RELOAD_KEY, String(now))
        window.location.reload()
      }

      throw err
    })
  )
}

// Root route redirects unauthenticated visitors ke marketing site (tradertekno.com).
// Logged-in users handled di PublicRoute (redirect ke /dashboard sebelum reach RedirectToMarketing).
// 14 Mei: app subdomain landing tidak SEO target; marketing site .com = canonical SEO source.
// Pre-fix: HomePage.tsx rendered duplicate "trading"-flavored landing (CLAUDE.md HARD RULE risk).
const RedirectToMarketing: React.FC = () => {
  React.useEffect(() => {
    window.location.replace('https://tradertekno.com')
  }, [])
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="text-slate-600 text-sm">Mengarahkan ke tradertekno.com…</div>
      </div>
    </div>
  )
}

// Lazy load pages for better performance
const LoginPage = lazyWithRetry(() => import('./pages/LoginPage'))
const RegisterPage = lazyWithRetry(() => import('./pages/RegisterPage'))
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage'))
const OnboardingPage = lazyWithRetry(() => import('./pages/OnboardingPage'))
const ImprovedDashboardPage = lazyWithRetry(() => import('./pages/ImprovedDashboardPage'))
// Sinyal / Sinyal Detail / Performa DICABUT 1 Juni 2026 (OJK cabut-total). Rutenya kini
// Navigate ke /dashboard; berkas placeholder-nya dihapus 23 Agu 2026 setelah 83 hari
// menganggur tanpa impor dan tanpa rute. Kode asli ada di riwayat git pra-23 Mei.
const NewsPage = lazyWithRetry(() => import('./pages/NewsPage'))
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage'))
const PreferencesPage = lazyWithRetry(() => import('./pages/PreferencesPage'))
const DataPrivasiPage = lazyWithRetry(() => import('./pages/DataPrivasiPage'))
const DisclaimerPage = lazyWithRetry(() => import('./pages/DisclaimerPage'))
const PrivacyPage = lazyWithRetry(() => import('./pages/PrivacyPage'))
// const SubscriptionPage = lazyWithRetry(() => import('./pages/SubscriptionPageBombastic'))
const SubscriptionLanding = lazyWithRetry(() => import('./components/landing/SubscriptionLanding'))
const AppLayout = lazyWithRetry(() => import('./components/layout/AppLayout'))

// Payment pages
const PaymentSuccessPage = lazyWithRetry(() => import('./pages/payment/PaymentSuccessPage'))
const PaymentPendingPage = lazyWithRetry(() => import('./pages/payment/PaymentPendingPage'))
const PaymentErrorPage = lazyWithRetry(() => import('./pages/payment/PaymentErrorPage'))
const TestPaymentPage = lazyWithRetry(() => import('./pages/TestPaymentPage'))

// User Management pages
const UserSubscriptionDashboard = lazyWithRetry(() => import('./pages/UserSubscriptionDashboard'))
const UsageAnalyticsPage = lazyWithRetry(() => import('./pages/UsageAnalyticsPage'))

// Tier Comparison page
const TierComparisonPage = lazyWithRetry(() => import('./pages/TierComparisonPage'))

// Referral page
const ReferralPage = lazyWithRetry(() => import('./pages/ReferralPage'))

// Risk Dashboard Portofolio (Tier 1 pure, stateless)
const PortfolioRiskPage = lazyWithRetry(() => import('./pages/PortfolioRiskPage'))

// Screener Saham (Tier 1, filter user-defined; nav entry menyusul saat data fundamental ingest)
const ScreenerPage = lazyWithRetry(() => import('./pages/ScreenerPage'))

// Test pages
const FreemiumTestPage = lazyWithRetry(() => import('./pages/FreemiumTestPage'))

// Protected route wrapper component
interface ProtectedRouteProps {
  children: React.ReactNode
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading, userProfile } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    // Bawa tujuan asli ke halaman login. Tanpa ini, deep link dari situs marketing
    // (mis. /screener, /portofolio/risiko) selalu mendarat di /dashboard setelah
    // login — CTA-nya jadi janji kosong.
    const intended = `${window.location.pathname}${window.location.search}`
    const target = intended && intended !== '/' ? `/login?next=${encodeURIComponent(intended)}` : '/login'
    return <Navigate to={target} replace />
  }

  // Redirect to onboarding if user hasn't completed it
  if (userProfile && !userProfile.onboardingComplete && window.location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}

// Public route wrapper (redirects authenticated users)
interface PublicRouteProps {
  children: React.ReactNode
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { user, loading, userProfile } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // If user is authenticated, redirect to appropriate page
  if (user) {
    // Check if onboarding is needed
    if (userProfile && !userProfile.onboardingComplete) {
      return <Navigate to="/onboarding" replace />
    }
    // Pengguna yang sudah login membuka /login?next=... (mis. dari tautan marketing)
    // tetap harus mendarat di tujuan, bukan dipentalkan ke dashboard.
    const next = new URLSearchParams(window.location.search).get('next')
    return <Navigate to={resolveNextPath(next)} replace />
  }

  return <>{children}</>
}

/** GA4: track page views globally for ALL routes (public + protected).
 *  Deduplicates against the inline fallback page_view sent in index.html
 *  (window.__tt_pv_path stores pathname+search) so pre-hydration bounces
 *  are captured without double-counting the initial load for users who stay. */
function GA4PageTracker() {
  const location = useLocation();
  useEffect(() => {
    const fullPath = location.pathname + location.search;
    const w = window as unknown as Record<string, unknown>;
    // Skip if inline script already sent page_view for this exact full path
    if (w.__tt_pv_path === fullPath) {
      delete w.__tt_pv_path;
      return;
    }
    delete w.__tt_pv_path;
    trackPageView(fullPath);
  }, [location.pathname, location.search]);
  return null;
}

/** Capture ?ref= from URL, store in localStorage, fire click tracking to backend */
function ReferralCapture() {
  useReferralCapture();
  return null;
}

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <LoadingSpinner size="lg" />
      </div>
    }>
      <GA4PageTracker />
      <ReferralCapture />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          <PublicRoute>
            <RedirectToMarketing />
          </PublicRoute>
        } />
        <Route path="/login" element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } />
        <Route path="/register" element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } />
        <Route path="/forgot-password" element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        } />
        
        {/* Landing Page for Subscription */}
        <Route path="/berlangganan" element={<SubscriptionLanding />} />

        {/* Public Legal Pages — wrapped in AppLayout so the top nav stays consistent.
            AppLayout gracefully renders without an authed user for public access. */}
        <Route path="/disclaimer" element={<AppLayout><DisclaimerPage /></AppLayout>} />
        <Route path="/privacy" element={<AppLayout><PrivacyPage /></AppLayout>} />

        {/* Protected Routes */}
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        } />

        {/* Main App Routes (with layout) */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <AppLayout>
              <ImprovedDashboardPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* /signals + /signals/detail REMOVED 1 Juni 2026 OJK cabut-total — redirect ke /dashboard */}
        <Route path="/signals" element={<Navigate to="/dashboard" replace />} />
        <Route path="/signals/detail" element={<Navigate to="/dashboard" replace />} />
        <Route path="/news" element={
          <ProtectedRoute>
            <AppLayout>
              <NewsPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* /performance REMOVED 1 Juni 2026 OJK cabut-total — redirect ke /dashboard */}
        <Route path="/performance" element={<Navigate to="/dashboard" replace />} />
        <Route path="/settings" element={
          <ProtectedRoute>
            <AppLayout>
              <SettingsPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/preferences" element={
          <ProtectedRoute>
            <AppLayout>
              <PreferencesPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/akun/data-saya" element={
          <ProtectedRoute>
            <AppLayout>
              <DataPrivasiPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* Public subscription page removed - use /account/subscription instead */}
        <Route path="/tier-comparison" element={
          <ProtectedRoute>
            <TierComparisonPage />
          </ProtectedRoute>
        } />
        <Route path="/account/subscription" element={
          <ProtectedRoute>
            <AppLayout>
              <UserSubscriptionDashboard />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/account/analytics" element={
          <ProtectedRoute>
            <AppLayout>
              <UsageAnalyticsPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/referral" element={
          <ProtectedRoute>
            <AppLayout>
              <ReferralPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/portofolio/risiko" element={
          <ProtectedRoute>
            <AppLayout>
              <PortfolioRiskPage />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/screener" element={
          <ProtectedRoute>
            <AppLayout>
              <ScreenerPage />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* Payment Routes - Public but with layout */}
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/pending" element={<PaymentPendingPage />} />
        <Route path="/payment/error" element={<PaymentErrorPage />} />

        {/* Test Routes - Development only */}
        {import.meta.env.DEV && (
          <>
            <Route path="/test/freemium" element={
              <ProtectedRoute>
                <FreemiumTestPage />
              </ProtectedRoute>
            } />
            <Route path="/test-payment" element={
              <ProtectedRoute>
                <TestPaymentPage />
              </ProtectedRoute>
            } />
          </>
        )}

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

const App: React.FC = () => {
  useEffect(() => {
    // Initialize accessibility features on app startup
    initializeAccessibility();
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <PricingProvider>
              <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <div role="main">
              <AppRoutes />
            </div>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#1f2937',
                  color: '#f9fafb',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  minHeight: '48px',
                  padding: '12px 16px'
                },
                success: {
                  iconTheme: {
                    primary: '#059669',
                    secondary: '#f9fafb'
                  },
                  style: {
                    background: '#065f46',
                    color: '#ffffff'
                  }
                },
                error: {
                  iconTheme: {
                    primary: '#dc2626',
                    secondary: '#f9fafb'
                  },
                  style: {
                    background: '#991b1b',
                    color: '#ffffff'
                  }
                }
              }}
              containerStyle={{
                zIndex: 9999
              }}
            />
              </BrowserRouter>
            </PricingProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  )
}

export default App